import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import { isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { decoratorColorToTrellisTokenMap } from 'customer-data-property-utils/optionDecoratorUtils';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { makeOptionsFromProperty, makeOptionsFromPropertyWithoutBlankOptions } from 'customer-data-property-utils/PropertyValueDisplay';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { memo, useCallback, useMemo } from 'react';
import { virtualizedMenuRenderer } from 'foundations-components/transitional/utils/virtualizedMenuRenderer';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { useObjectTypeId } from '../v2/hooks/useObjectTypeId';
import { useExcludedEnumOptionsForProperty } from '../hooks/useExcludedEnumOptions';
import SelectWithIndicator from './select/SelectWithIndicator';
import useOptionColors, { decoratorColorToBadgeMap, decoratorColorToTagMap } from './select/useOptionColors';
import { useGetIsUngated } from '../v2/hooks/useGetIsUngated';
import { gate } from 'hub-http/gates';
import { jsx as _jsx } from "react/jsx-runtime";
export const TEST_UTILS = {
  captureOptionsForTesting: options => options
};
const PropertyInputEnum = props => {
  const {
    defaultValue,
    isRequired,
    inputRef,
    placeholder,
    emptyOptionLabel,
    onChange,
    transformOption,
    onOpenChange,
    formControlUse
  } = props;
  const property = useProperty();
  const value = usePropertyValue();
  const objectTypeId = useObjectTypeId();
  useComponentMountMetric('PropertyInputEnum');
  const handleChange = useCallback(evt => {
    const _value = evt.target.value;
    onChange(syntheticEvent(Array.isArray(_value) ? _value.join(';') : _value));
  }, [onChange]);
  const {
    colorByOptionValue,
    shouldUseOptionColors,
    shouldUseBadgeDisplay
  } = useOptionColors();
  const multi = isMultienum(property);
  const valueOrDefault = value || defaultValue;
  const parsedValue = multi ? parseMultiEnumValue(valueOrDefault) : valueOrDefault;
  const getIsUngated = useGetIsUngated();
  const isUngatedForTasksIndex = getIsUngated(gate('Tasks:CrmPlatform'));
  const excludedEnumOptions = useExcludedEnumOptionsForProperty({
    objectTypeId,
    propertyName: property.name,
    value: parsedValue
  });
  const options = useMemo(() => {
    // Add a placeholder option for optional single selects if one is not already present.
    // Multi selects don't need a placeholder (they can be cleared manually).
    // Required selects don't need a placeholder option (user must pick a real option).
    const uiSelectOptions = (multi || isRequired) && !emptyOptionLabel ? makeOptionsFromPropertyWithoutBlankOptions(property, transformOption) : makeOptionsFromProperty(property, transformOption);
    let selectOptions = uiSelectOptions.map(option => Object.assign({}, option, option.badge ? {
      badge: option.badge
    } : {}));

    // This is gated to avoid breaking existing behavior for this enum
    if (isUngatedForTasksIndex) {
      if (excludedEnumOptions.length > 0) {
        var _selectOptions;
        selectOptions = (_selectOptions = selectOptions) === null || _selectOptions === void 0 ? void 0 : _selectOptions.filter(option => !excludedEnumOptions.includes(option.value));
      }
    }
    if (emptyOptionLabel) {
      var _selectOptions2;
      selectOptions = (_selectOptions2 = selectOptions) === null || _selectOptions2 === void 0 ? void 0 : _selectOptions2.map(option => option.value === '' ? Object.assign({}, option, {
        text: emptyOptionLabel
      }) : option);
    }
    if (multi && Array.isArray(parsedValue)) {
      // Unfortunately `<UISelect multi={true}>` does not highlight values
      // which are invalid (values which do not match an option). Single
      // selects show a red border when their value is invalid, but multi
      // selects have no feedback.
      //
      // The BE prevents invalid values from being set on enumerations,
      // but does not block subsequent changes to the property definition
      // which remove in-use options.
      //
      // When we removed the red highlight, we immediately got JIRAs from
      // users who were confused why they couldn't save changes to some
      // properties (because they had invalid values).
      //
      // #hds-support confirmed that the best solution currently is to
      // manually detect invalid options and create temporary options
      // that give them a highlight color.
      //
      // See https://git.hubteam.com/HubSpot/UIComponents/issues/9868
      // See https://product.hubteam.com/docs/crm-properties/values/enumeration.html

      const validValues = new Set(property.options.map(option => option.value));
      const invalidValues = parsedValue.filter(val => !validValues.has(val));
      if (invalidValues.length) {
        return [...(selectOptions || []), ...invalidValues.map(val => {
          return {
            text: val || '',
            value: val || undefined,
            tagUse: 'candy-apple'
          };
        })];
      }
    }
    if (shouldUseOptionColors && !shouldUseBadgeDisplay) {
      var _selectOptions3;
      selectOptions = (_selectOptions3 = selectOptions) === null || _selectOptions3 === void 0 ? void 0 : _selectOptions3.map(option => {
        if (!option.value) {
          return option;
        }
        const optionColor = colorByOptionValue[String(option.value)];
        const colorAttribute = multi ? {
          tagUse: (optionColor === null || optionColor === void 0 ? void 0 : optionColor.tagColor) || decoratorColorToTagMap.GRAY
        } : {
          indicator: /*#__PURE__*/_jsx(StatusDot, {
            color: (optionColor === null || optionColor === void 0 ? void 0 : optionColor.trellisColor) || decoratorColorToTrellisTokenMap.GRAY
          })
        };
        return Object.assign({}, option, colorAttribute);
      });
    }
    return selectOptions;
  }, [colorByOptionValue, emptyOptionLabel, isRequired, multi, parsedValue, property, shouldUseBadgeDisplay, shouldUseOptionColors, transformOption, isUngatedForTasksIndex, excludedEnumOptions]);
  const ItemComponent = useMemo(() => {
    if (!shouldUseBadgeDisplay) {
      return undefined;
    }
    return _ref => {
      let {
          children,
          option
        } = _ref,
        restItemComponent = _objectWithoutPropertiesLoose(_ref, _excluded);
      if (!option.value) {
        return /*#__PURE__*/_jsx("div", Object.assign({}, restItemComponent, {
          children: children
        }));
      }
      const optionColor = colorByOptionValue[option.value];
      const badgeColor = (optionColor === null || optionColor === void 0 ? void 0 : optionColor.badgeColor) || decoratorColorToBadgeMap.GRAY;
      return /*#__PURE__*/_jsx(View, Object.assign({
        paddingBlock: "200",
        paddingInline: "500"
      }, restItemComponent, {
        children: /*#__PURE__*/_jsx(Badge, {
          use: badgeColor,
          "data-test-badge-use": badgeColor,
          "data-test-id": "badge",
          children: option.text
        })
      }));
    };
  }, [colorByOptionValue, shouldUseBadgeDisplay]);
  const ValueRenderer = useMemo(() => {
    if (!shouldUseBadgeDisplay) {
      return undefined;
    }
    return option => {
      if (!option.value) {
        return option.buttonText;
      }
      const optionColor = colorByOptionValue[option.value];
      const badgeColor = (optionColor === null || optionColor === void 0 ? void 0 : optionColor.badgeColor) || decoratorColorToBadgeMap.GRAY;
      return /*#__PURE__*/_jsx("span", {
        style: {
          verticalAlign: 'bottom'
        },
        children: /*#__PURE__*/_jsx(Badge, {
          use: badgeColor,
          "data-test-badge-use": badgeColor,
          "data-test-id": "badge",
          children: option.text
        })
      });
    };
  }, [colorByOptionValue, shouldUseBadgeDisplay]);
  const MenuRenderer = useMemo(() => {
    // We can't use a virtualizedMenuRenderer when we have a custom itemComponent to render the badge
    if (shouldUseBadgeDisplay) {
      return undefined;
    }

    // Only use virtualization for large option sets
    if (options && options.length > 50) {
      return virtualizedMenuRenderer;
    }
    return undefined;
  }, [shouldUseBadgeDisplay, options]);

  // There is not a great way to assert on the tagUse attribute of the options just by inspecting the rendered output since all multi selects renders options as tags
  // And colored tags are indistinguishable from regular tags in the DOM
  // This gives us a chance to inspect the resulting options with minimal hassle
  TEST_UTILS.captureOptionsForTesting(options);
  const placeholderFallback = formControlUse === 'floating' ? undefined : '';
  return /*#__PURE__*/_jsx(SelectWithIndicator, Object.assign({}, getTransferrableProps(props), {
    onChange: handleChange,
    multi: multi,
    anchorType: "button",
    options: options,
    placeholder: placeholder || placeholderFallback,
    inputRef: inputRef,
    value: parsedValue,
    onOpenChange: onOpenChange,
    itemComponent: ItemComponent,
    valueRenderer: ValueRenderer,
    menuRenderer: MenuRenderer
  }));
};
export default /*#__PURE__*/memo(PropertyInputEnum);